import { useCallback, useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Avatar, Box, Button, Card, Chip, IconButton, LinearProgress, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';
import Page from '../../components/Page';
import StageStepper from './components/StageStepper';
import { RetentionContext } from './RetentionContext';
import { PHASES, TOTAL_STAGES, WORKSPACE_TABS, getWorkflowStageNo, stageByNo } from './constants/stages';
import { getRetentionHeader, getRetentionStages, resetRetentionWorkspace } from '../../redux/features/retentionSlice';
import { HERO_GRADIENT, RT } from './theme/retentionTheme';
import { EmptyState } from './components/ui';

const ROW_CACHE_KEY = (id) => `retention-row-${id}`;

// The CRO Leads row passed through navigate(state) survives a refresh via sessionStorage.
const readCachedRow = (id) => {
  try {
    const raw = sessionStorage.getItem(ROW_CACHE_KEY(id));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeCachedRow = (id, row) => {
  try {
    sessionStorage.setItem(ROW_CACHE_KEY(id), JSON.stringify(row));
  } catch (e) {
    // storage unavailable; the header API is the real source anyway
  }
};

const initials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');

const glassBtn = { color: '#fff', bgcolor: 'rgba(255,255,255,.14)', '&:hover': { bgcolor: 'rgba(255,255,255,.24)' } };

// Workspace shell for /student-journey/:courseEnrollmentId/*, rendered inside the portal layout (RETENTION_WORKFLOW.md §2.2).
export default function RetentionLayout() {
  const { courseEnrollmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { header, stages } = useSelector((state) => state.retention);

  const stateRow = location.state?.row;
  useEffect(() => {
    if (stateRow) writeCachedRow(courseEnrollmentId, stateRow);
  }, [stateRow, courseEnrollmentId]);

  const refresh = useCallback(() => {
    dispatch(getRetentionHeader(courseEnrollmentId));
    dispatch(getRetentionStages(courseEnrollmentId));
  }, [dispatch, courseEnrollmentId]);

  useEffect(() => {
    refresh();
    return () => dispatch(resetRetentionWorkspace());
  }, [refresh, dispatch]);

  // memoized: readCachedRow() parses a new object each call, which would re-render every consumer
  const student = useMemo(
    () => header.data || stateRow || readCachedRow(courseEnrollmentId),
    [header.data, stateRow, courseEnrollmentId],
  );

  const progressByStage = useMemo(() => {
    const map = {};
    (Array.isArray(stages.data) ? stages.data : []).forEach((s) => {
      if (s?.stage_no) map[s.stage_no] = s;
    });
    return map;
  }, [stages.data]);

  // retention_stage_no from /header when built, else derived from the CRO Leads row (Admission Confirmed = stage 7).
  const currentStage = getWorkflowStageNo({ ...student, ...header.data });
  const stage = stageByNo(currentStage);
  const phase = PHASES[stage?.phase || 1];

  const activeTab = useMemo(() => {
    const seg = location.pathname.split('/').filter(Boolean)[2] || '';
    return WORKSPACE_TABS.some((t) => t.value === seg) ? seg : '';
  }, [location.pathname]);

  const ctx = useMemo(
    () => ({
      courseEnrollmentId,
      student,
      currentStage,
      progressByStage,
      headerReady: Boolean(header.data),
      refresh,
    }),
    [courseEnrollmentId, student, currentStage, progressByStage, header.data, refresh],
  );

  const phone = student?.contactNumber;
  const waNumber = phone ? String(phone).replace(/[^\d]/g, '') : '';
  const name = student?.name || student?.student_name || 'Student';
  const meta = [
    { icon: 'solar:hashtag-square-bold', text: student?.admn_no },
    { icon: 'solar:square-academic-cap-bold', text: student?.class },
    { icon: 'solar:book-bookmark-bold', text: student?.assigned_course_name },
    { icon: 'solar:phone-bold', text: phone },
  ].filter((m) => m.text);
  const pct = Math.round(((currentStage - 1) / (TOTAL_STAGES - 1)) * 100);

  // §6.0: the API answers 403 / 404 for students outside the caller's scope.
  const blocked = ['forbidden', 'not_found'].includes(header.error?.reason);
  if (blocked) {
    return (
      <Page title="Retention workspace">
        <Card sx={{ maxWidth: 520, mx: 'auto', mt: 6, p: 3, textAlign: 'center' }}>
          <EmptyState
            icon={header.error.reason === 'forbidden' ? 'solar:lock-keyhole-bold-duotone' : 'solar:file-remove-bold-duotone'}
            color={RT.error}
            title={header.error.message}
          />
          <Button variant="contained" startIcon={<Icon icon="solar:arrow-left-linear" width={18} />} onClick={() => navigate('/my-workspace/cro-leads')}>
            Back to CRO Leads
          </Button>
        </Card>
      </Page>
    );
  }
  const schemaMissing = stages.error?.reason === 'schema_missing';

  return (
    <Page title="Retention workspace">
      <Stack gap={2.5}>
        {schemaMissing && (
          <Alert severity="info" icon={<Icon icon="solar:database-bold-duotone" width={20} />}>
            Retention tables aren't on this database yet, so stage history and stage actions are off.
          </Alert>
        )}
        {!student && (
          <Alert severity="warning" icon={<Icon icon="solar:danger-triangle-bold" width={20} />}>
            Open this workspace from CRO Leads to load the student.
          </Alert>
        )}

        {/* student hero + stepper */}
        <Card sx={{ overflow: 'hidden' }}>
          <Box sx={{ background: HERO_GRADIENT, color: '#fff', px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.5 }, position: 'relative' }}>
            <Box
              sx={{
                position: 'absolute',
                right: -60,
                top: -80,
                width: 260,
                height: 260,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,255,255,.18) 0%, rgba(255,255,255,0) 70%)',
                pointerEvents: 'none',
              }}
            />
            <Stack direction="row" alignItems="center" gap={1} mb={2}>
              <Tooltip title="Back to CRO Leads">
                <IconButton size="small" onClick={() => navigate('/my-workspace/cro-leads')} sx={glassBtn}>
                  <Icon icon="solar:arrow-left-linear" width={18} />
                </IconButton>
              </Tooltip>
              <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 800, letterSpacing: '.06em' }}>
                RETENTION WORKSPACE · CE-{courseEnrollmentId}
              </Typography>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} gap={2.5}>
              <Stack direction="row" alignItems="center" gap={2} sx={{ flex: 1, minWidth: 0 }}>
                <Avatar sx={{ width: 60, height: 60, fontSize: 22, bgcolor: '#fff', color: RT.primary, boxShadow: '0 8px 20px -8px rgba(0,0,0,.4)' }}>
                  {initials(name)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h4" sx={{ color: '#fff' }} noWrap>
                    {name}
                  </Typography>
                  <Stack direction="row" gap={0.75} flexWrap="wrap" mt={0.75}>
                    {meta.map((m) => (
                      <Chip
                        key={m.icon}
                        size="small"
                        label={m.text}
                        icon={<Icon icon={m.icon} width={14} />}
                        sx={{ bgcolor: 'rgba(255,255,255,.16)', color: '#fff', '& .MuiChip-icon': { color: '#fff', ml: '6px' } }}
                      />
                    ))}
                  </Stack>
                </Box>
              </Stack>

              <Stack gap={1.25} sx={{ minWidth: { md: 300 } }}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#fff', color: phase.color }}>
                    <Icon icon={stage?.icon} width={18} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, opacity: 0.8, letterSpacing: '.06em' }} noWrap>
                      STAGE {currentStage}/{TOTAL_STAGES} · {phase.label.toUpperCase()}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: 15 }} noWrap>
                      {stage?.short}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 18 }}>{pct}%</Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  sx={{ bgcolor: 'rgba(255,255,255,.22)', '& .MuiLinearProgress-bar': { bgcolor: '#fff' } }}
                />
                <Stack direction="row" gap={1}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Icon icon="solar:phone-calling-bold" width={18} />}
                    href={phone ? `tel:${phone}` : undefined}
                    disabled={!phone}
                    sx={{
                      bgcolor: '#fff',
                      color: `${RT.primary} !important`,
                      fontWeight: 800,
                      boxShadow: '0 6px 16px -8px rgba(0,0,0,.45)',
                      '& .MuiButton-startIcon': { color: RT.primary },
                      '&:hover': { bgcolor: RT.primaryLight, boxShadow: '0 6px 16px -8px rgba(0,0,0,.45)' },
                      '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,.35)', color: 'rgba(255,255,255,.8) !important' },
                    }}
                  >
                    Call
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Icon icon="ic:baseline-whatsapp" width={18} />}
                    href={waNumber ? `https://wa.me/${waNumber}` : undefined}
                    target="_blank"
                    rel="noreferrer"
                    disabled={!waNumber}
                    sx={{
                      bgcolor: RT.whatsapp,
                      color: '#fff !important',
                      fontWeight: 800,
                      boxShadow: '0 6px 16px -8px rgba(0,0,0,.45)',
                      '&:hover': { bgcolor: '#15803D', boxShadow: '0 6px 16px -8px rgba(0,0,0,.45)' },
                      '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,.35)', color: 'rgba(255,255,255,.8) !important' },
                    }}
                  >
                    WhatsApp
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 1.5, md: 2 } }}>
            <StageStepper
              currentStage={currentStage}
              progress={progressByStage}
              onStageClick={(s) => navigate(`/student-journey/${courseEnrollmentId}/${s.tab}?stage=${s.no}`)}
            />
          </Box>
        </Card>

        {/* tabs */}
        <Card sx={{ p: 0.75 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => navigate(`/student-journey/${courseEnrollmentId}${v ? `/${v}` : ''}`)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {WORKSPACE_TABS.map((t) => (
              <Tab
                key={t.value || 'home'}
                value={t.value}
                iconPosition="start"
                icon={<Icon icon={t.icon} width={18} />}
                label={
                  <Stack direction="row" alignItems="center" gap={0.75}>
                    {t.label}
                    {t.stages && (
                      <Box component="span" sx={{ fontSize: 11, fontWeight: 800, opacity: 0.65 }}>
                        {t.stages}
                      </Box>
                    )}
                  </Stack>
                }
                sx={{ '& .MuiTab-iconWrapper': { mr: 0.75 } }}
              />
            ))}
          </Tabs>
        </Card>

        <RetentionContext.Provider value={ctx}>
          <Outlet />
        </RetentionContext.Provider>
      </Stack>
    </Page>
  );
}
